// components/avatar-helpers.js: pure prop math used by AvatarCircle.
//
// Extracted so node:test (which doesn't transform JSX) can cover the
// rendering rules, color fallback, letter casing/fallback, label
// composition, font sizing, without spinning up jsdom.

import { AVATAR_COLORS, DEFAULT_AVATAR_COLOR } from '../identity/avatar-colors.js';

/**
 * Pick a valid avatar color key, falling back to the default for unknown
 * inputs.
 *
 * @param {unknown} color
 * @returns {import('../identity/avatar-colors.js').AvatarColorKey}
 */
export function pickColorKey(color) {
  if (typeof color === 'string' && Object.prototype.hasOwnProperty.call(AVATAR_COLORS, color)) {
    return /** @type {any} */ (color);
  }
  return DEFAULT_AVATAR_COLOR;
}

/**
 * The single uppercase initial drawn inside the circle. Whitespace-only
 * and empty names fall back to a question mark so the circle is never
 * blank.
 *
 * @param {string|undefined|null} name
 * @returns {string}
 */
export function pickAvatarLetter(name) {
  const trimmed = (name || '').trim();
  return trimmed.length > 0 ? trimmed[0].toUpperCase() : '?';
}

/**
 * Compose the screen-reader label. Callers may override with `explicit`
 * when the surrounding context already names the user (e.g., a list
 * item that has the name as visible text adjacent).
 *
 * @param {string|undefined|null} name
 * @param {import('../identity/avatar-colors.js').AvatarColorKey} colorKey
 * @param {string|undefined} explicit
 * @returns {string}
 */
export function composeAvatarLabel(name, colorKey, explicit) {
  if (explicit) return explicit;
  const trimmed = (name || '').trim();
  const colorLabel = AVATAR_COLORS[colorKey].label;
  return trimmed.length > 0
    ? `${trimmed} avatar (${colorLabel})`
    : `Empty avatar (${colorLabel})`;
}

/**
 * B3b item 8: the accessible name of the identity badge button. It says
 * what the button does; the avatar picture inside it is not the name.
 *
 * @param {string|undefined|null} name
 * @returns {string}
 */
export function identityBadgeLabel(name) {
  const trimmed = (name || '').trim();
  return trimmed.length > 0
    ? `Edit your name and colour (${trimmed})`
    : 'Edit your name and colour';
}

/**
 * Font size inside the circle, scaled from the requested diameter.
 *
 * @param {number} size
 * @returns {number}
 */
export function avatarFontSize(size) {
  return Math.round(size * 0.5);
}
